import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { login as loginApi, logout as logoutApi, getUserInfo } from '../api/auth.api';
import type { LoginCredentials, UserInfo } from '../types/auth.types';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthenticated, clearAuth } from '@/store/slices/authSlice';
import type { RootState } from '@/store/store';

export const useAuthState = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [isInitializing, setIsInitializing] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const info = await getUserInfo();
        setUserInfo(info);
        dispatch(setAuthenticated());
      } catch (error) {
        dispatch(clearAuth());
      } finally {
        setIsInitializing(false);
      }
    };
    initAuth();
  }, [dispatch]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      await loginApi(credentials);

      const info = await getUserInfo();
      setUserInfo(info);
      dispatch(setAuthenticated());

      if (info.admin) toast.success("Connecté en tant qu'administrateur");
      if (info.need_pwd_change) setShowPasswordModal(true);

      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Échec de la connexion');
      return false;
    }
  }, [dispatch]);

  const logout = useCallback(async () => {
    try {
      await logoutApi(); 
    } finally {
        dispatch(clearAuth());
        setUserInfo(null);
        setShowPasswordModal(false);
    }
  }, [dispatch]);

  return {
    isAuthenticated,
    isInitializing,
    login,
    logout,
    userInfo,
    showPasswordModal,
    setShowPasswordModal,
  };
};